import { useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponse } from "../models/system";
import {
  ISingleVehicleResponse,
  IVehicle,
  IVehicleLookupRequest,
  IVehicleLookupRequestResponse,
  VehicleLookupMode,
} from "../models/Vehicle";
import { vehicleExistedBeforeLookup } from "../utils/vehicleIdentifier";
import { lookupOutcome, pollVehicleLookup } from "../utils/vehicleLookupPolling";
import useVehicleLookupConfig from "./useVehicleLookupConfig";

export interface IVehicleLookupVariables {
  identifier: string;
  mode?: VehicleLookupMode;
  /** Optional plate typed alongside a VIN. */
  registration?: string;
  /** Refresh this vehicle in place instead of creating one. */
  vehicleId?: string;
  signal?: AbortSignal;
}

export interface IVehicleRefreshVariables {
  vehicleId: string;
  vrm?: string;
  vin?: string;
}

// Compatible with ISingleVehicleResponse so every existing caller's `data.data` still works, plus
// what the create pages need to know: did the vehicle exist before this lookup.
export interface IVehicleLookupResponse extends DefaultResponse {
  data: IVehicle | null;
  existed: boolean;
  request: IVehicleLookupRequest | null;
}

const POLL_MS = 1500;
const MAX_POLL_MS = 60000;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const is404 = (error: any) => !!error?.message?.includes("HTTP Error: 404");

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * One lookup for both modes. Synchronous providers answer the GET that has always served this;
 * asynchronous ones go through POST /vehicle/lookup, which waits for the provider server-side and
 * is only polled when that wait hands back a request still pending.
 */
export default function useVehicleLookup() {
  const { get, patch, post } = useApiHelper();
  const queryClient = useQueryClient();
  const { lookupConfig } = useVehicleLookupConfig();

  const invalidate = () => {
    queryClient.invalidateQueries(["vehicles"]);
    queryClient.invalidateQueries(["lead"]);
    queryClient.invalidateQueries(["pagedTable", "/vehicle"]);
  };

  const lookupSync = async (vars: IVehicleLookupVariables): Promise<IVehicleLookupResponse> => {
    const mode = vars.mode ?? "CreateIfMissing";
    const query = mode === "CreateIfMissing" ? "" : `?mode=${mode}`;

    try {
      const response = await get<ISingleVehicleResponse>(`/vehicle/lookup/${encodeURIComponent(vars.identifier)}${query}`);

      return {
        success: response.success,
        message: response.message,
        data: response.data ?? null,
        existed: vehicleExistedBeforeLookup(response.data),
        request: null,
      };
    } catch (error: any) {
      // The non-creating modes answer a miss with 404; that is "not held", not an error.
      if (mode !== "CreateIfMissing" && is404(error)) {
        return { success: false, message: "Vehicle not found", data: null, existed: false, request: null };
      }

      throw error;
    }
  };

  const lookupAsync = async (vars: IVehicleLookupVariables): Promise<IVehicleLookupResponse> => {
    const mode = vars.mode ?? "CreateIfMissing";
    const body = {
      identifier: vars.identifier,
      mode,
      registration: vars.registration || undefined,
      vehicleId: vars.vehicleId || undefined,
    };

    const request = await pollVehicleLookup(
      {
        start: async () => (await post<typeof body, IVehicleLookupRequestResponse>("/vehicle/lookup", body)).data,
        fetchRequest: async (id) => (await get<IVehicleLookupRequestResponse>(`/vehicle/lookup/requests/${id}`)).data,
        sleep,
        now: Date.now,
        signal: vars.signal,
      },
      { pollMs: POLL_MS, maxMs: MAX_POLL_MS }
    );

    const outcome = lookupOutcome(request, mode);

    return {
      success: !!outcome.vehicle,
      message: request.error ?? String(request.status),
      data: outcome.vehicle,
      existed: outcome.existed,
      request,
    };
  };

  const lookup = async (vars: IVehicleLookupVariables): Promise<IVehicleLookupResponse> => {
    const mode = vars.mode ?? "CreateIfMissing";

    // "Do we already hold this one" never needs the provider, whichever mode the yard is in.
    const response =
      mode === "ExistingOnly" || !lookupConfig.asynchronous ? await lookupSync({ ...vars, mode }) : await lookupAsync({ ...vars, mode });

    if (response.data && mode === "CreateIfMissing" && !response.existed) invalidate();

    return response;
  };

  const refresh = async (vars: IVehicleRefreshVariables): Promise<void> => {
    if (lookupConfig.asynchronous) {
      if (vars.vin) {
        await lookupAsync({ identifier: vars.vin, vehicleId: vars.vehicleId, registration: vars.vrm });
      } else {
        // A VIN-keyed provider has nothing to say about a plate, so correcting the registration on
        // such a yard just saves it on the vehicle; the VIN data already held stays as it is.
        await patch(`/vehicle/${vars.vehicleId}`, { vrm: vars.vrm });
      }
    } else {
      await post(`/vehicle/${vars.vehicleId}/refresh`, { vrm: vars.vrm, vin: vars.vin });
    }

    invalidate();
  };

  // Scans carry the vehicle id; typed input carries an identifier. The id is authoritative when
  // it resolves, and a VIN-keyed vehicle with no plate can only be reached that way from a label.
  const resolveExisting = async (target: { id?: string | null; identifier?: string | null }): Promise<IVehicle | null> => {
    if (target.id && GUID.test(target.id)) {
      try {
        const response = await get<ISingleVehicleResponse>(`/vehicle/${target.id}`);
        if (response?.data) return response.data;
      } catch (error: any) {
        if (!is404(error)) throw error;
      }
    }

    if (target.identifier) return (await lookupSync({ identifier: target.identifier, mode: "ExistingOnly" })).data;

    return null;
  };

  return {
    lookup,
    refresh,
    resolveExisting,
    config: lookupConfig,
  };
}
